import type { Command, CommandResult, CommandSink } from '../sim/commands';
import type { PlayerId } from '../sim/ids';
import type { Simulation } from '../sim/Simulation';
import { createMatchSim } from './matchFactory';
import { PROTOCOL_VERSION, SERVER_TICK_RATE, type ClientMessage, type LobbySeat, type MatchStart, type RoomOptions, type ServerMessage, type TickEntry } from './protocol';

export interface NetCallbacks {
  onLobby?(seats: readonly LobbySeat[], options: RoomOptions, started: boolean): void;
  /** The match begins (first time only): boot the game with this Simulation. */
  onStart?(sim: Simulation, me: PlayerId, start: MatchStart): void;
  onReject?(message: string): void;
  onError?(code: string, message: string): void;
  onStatus?(status: 'connecting' | 'online' | 'reconnecting' | 'offline'): void;
  onDesync?(tick: number, local: string, server: string): void;
}

/**
 * Browser side of an online match (Phase 10, DECISIONS D87–D92): one
 * WebSocket to the room, a session token kept for reconnects, and a queue of
 * server ticks. The game steps its Simulation ONLY through `pump`, one server
 * tick at a time, applying exactly the commands the server accepted for that
 * tick — so every client holds the same state.
 */
export class NetSession {
  ws: WebSocket | null = null;
  me: PlayerId | null = null;
  sim: Simulation | null = null;
  private seq = 0;
  private readonly queue: { tick: number; cmds: TickEntry['cmds'] }[] = [];
  private readonly serverHashes = new Map<number, string>();
  private lastTickAt = 0;
  private closed = false;
  private retry = 0;
  private started = false;
  private nextTick = 0;
  /** Registered profile sent with hello (raids, rankings). */
  profile: { id: string; token: string } | undefined;
  /** Called once after the server welcomed us. */
  afterWelcome: (() => void) | undefined;
  /** Ticks this client has stepped (for tests / debug). */
  desyncs = 0;

  constructor(
    readonly server: string,
    readonly room: string,
    private readonly name: string,
    private readonly cb: NetCallbacks,
  ) {}

  private get sessionKey(): string {
    return `atf-session-${this.room}`;
  }

  connect(): void {
    this.cb.onStatus?.(this.retry ? 'reconnecting' : 'connecting');
    const ws = new WebSocket(`${this.server.replace(/\/$/, '')}/rooms/${this.room}`);
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      let session: string | undefined;
      try {
        session = sessionStorage.getItem(this.sessionKey) ?? undefined;
      } catch {
        session = undefined;
      }
      this.send({ t: 'hello', v: PROTOCOL_VERSION, name: this.name, ...(session ? { session } : {}), ...(this.profile ? { profile: this.profile } : {}) });
      this.cb.onStatus?.('online');
    };
    ws.onmessage = (e) => {
      try {
        this.onMessage(JSON.parse(String(e.data)) as ServerMessage);
      } catch (err) {
        console.error('[net] bad message', err);
      }
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.closed) return this.cb.onStatus?.('offline');
      // Reconnect with backoff (1, 2, 4, 8 s …, capped at 8 s); the server keeps our seat for 90 s in a match.
      this.retry++;
      this.cb.onStatus?.('reconnecting');
      setTimeout(() => !this.closed && this.connect(), Math.min(8000, 1000 * 2 ** (this.retry - 1)));
    };
  }

  close(): void {
    this.closed = true;
    this.ws?.close(1000);
  }

  setReady(ready: boolean): void {
    this.send({ t: 'ready', ready });
  }

  setOptions(options: RoomOptions): void {
    this.send({ t: 'options', options });
  }

  startMatch(): void {
    this.send({ t: 'start' });
  }

  leave(): void {
    this.send({ t: 'leave' });
    this.close();
  }

  /** Command sink for the game: validate locally for instant feedback, then let the server decide. */
  readonly sink: CommandSink = {
    submit: (cmd: Command): CommandResult => {
      if (!this.sim) return { ok: false, code: 'invalid_payload', message: 'Not connected.' };
      const v = this.sim.validate(cmd);
      if (!v.ok) return v;
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return { ok: false, code: 'invalid_payload', message: 'Connection lost — reconnecting…' };
      this.send({ t: 'cmd', seq: ++this.seq, cmd });
      return v;
    },
  };

  /**
   * Advance the local Simulation by the server ticks received so far (at most
   * `max` this frame, so a long catch-up never freezes the page). Returns how
   * many ticks were stepped.
   */
  pump(step: (dt: number) => void, max = 20, budgetMs = 0): number {
    const sim = this.sim;
    if (!sim) return 0;
    let n = 0;
    const t0 = performance.now();
    // Catching up (reconnect / long frame): keep stepping past `max` while within the time budget.
    while (this.queue.length && (n < max || (budgetMs > 0 && performance.now() - t0 < budgetMs))) {
      const t = this.queue.shift()!;
      for (const c of t.cmds) sim.apply(c.cmd);
      step(1 / SERVER_TICK_RATE);
      this.nextTick = t.tick + 1;
      n++;
      const h = this.serverHashes.get(t.tick);
      if (h !== undefined) {
        this.serverHashes.delete(t.tick);
        const local = sim.stateHash();
        if (local !== h) {
          this.desyncs++;
          console.error(`[net] desync at tick ${t.tick}: local ${local}, server ${h}`);
          this.cb.onDesync?.(t.tick, local, h);
        }
      }
    }
    if (n) this.lastTickAt = performance.now();
    return n;
  }

  /** Interpolation fraction since the last tick (0..1), for smooth rendering between server ticks. */
  get alpha(): number {
    return Math.max(0, Math.min(1, (performance.now() - this.lastTickAt) / (1000 / SERVER_TICK_RATE)));
  }

  get localTick(): number {
    return this.nextTick;
  }

  get backlog(): number {
    return this.queue.length;
  }

  // ------------------------------------------------------------------

  private onMessage(m: ServerMessage): void {
    switch (m.t) {
      case 'welcome':
        this.me = m.playerId;
        if (this.afterWelcome) {
          const f = this.afterWelcome;
          this.afterWelcome = undefined;
          f();
        }
        try {
          sessionStorage.setItem(this.sessionKey, m.session);
        } catch {
          /* private mode: no reconnect after a reload, but live reconnects still work */
        }
        break;
      case 'lobby':
        this.cb.onLobby?.(m.seats, m.options, m.started);
        break;
      case 'start':
        this.onStart(m.start, m.tick, m.log);
        break;
      case 'tick': {
        if (!this.started) break;
        const expected = this.nextTick + this.queue.length;
        if (m.tick === expected) this.queue.push({ tick: m.tick, cmds: m.cmds });
        else if (m.tick > expected) this.send({ t: 'resync' }); // a gap: ask for the log again
        break;
      }
      case 'hash':
        if (m.tick >= this.nextTick) this.serverHashes.set(m.tick, m.hash);
        else if (this.sim && m.tick === this.nextTick - 1 && this.sim.stateHash() !== m.hash) {
          this.desyncs++;
          this.cb.onDesync?.(m.tick, this.sim.stateHash(), m.hash);
        }
        break;
      case 'reject':
        this.cb.onReject?.(m.message);
        break;
      case 'error':
        this.cb.onError?.(m.code, m.message);
        break;
      default:
        break;
    }
  }

  /**
   * Match start, or a reconnect into a running match. First time: build the
   * Simulation and fast-forward through the command log to the server's tick.
   * Reconnect: our Simulation is already at `nextTick`; fill the gap from the log.
   */
  private onStart(start: MatchStart, serverTick: number, log: readonly TickEntry[]): void {
    const byTick = new Map(log.map((e) => [e.tick, e.cmds]));
    if (!this.started) {
      this.started = true;
      this.sim = createMatchSim(start, this.me ?? undefined);
      this.nextTick = 0;
    }
    // Ticks we have not stepped yet that the server already did: queue them (from the log) ahead of live ticks.
    this.queue.length = 0;
    for (let t = this.nextTick; t < serverTick; t++) this.queue.push({ tick: t, cmds: byTick.get(t) ?? [] });
    if (this.cb.onStart && this.sim && this.me) {
      const cb = this.cb.onStart;
      this.cb.onStart = undefined; // boot once
      cb(this.sim, this.me, start);
    }
  }

  private send(m: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }
}
